import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateInpatientRulesetMutation } from "store/queries/inpatientRulesets";

import {
  InpatientRulesetForm,
  IInpatientRulesetFormData,
  INITIAL_FORM_DATA,
} from "./components/InpatientRulesetForm";

export function CreateInpatientRulesetPage() {
  useDocumentTitle("Create Inpatient Ruleset");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateInpatientRulesetMutation();
  const [formData, setFormData] =
    useState<IInpatientRulesetFormData>(INITIAL_FORM_DATA);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const config =
        formData.type === "FIXED_COST"
          ? {
              costToChargeRatio: formData.costToChargeRatio!,
              fixedLossThresholdInCents: formData.fixedLossThresholdInCents!,
              outlierPercent: formData.outlierPercent!,
            }
          : {
              thresholdInCents: formData.thresholdInCents!,
              chargeMultiplier: formData.chargeMultiplier!,
            };

      const result = await createRuleset({
        providerId: formData.providerId!,
        payerId: formData.payerId!,
        name: formData.name.trim(),
        drgCodes: formData.drgCodes,
        type: formData.type,
        config,
      }).unwrap();

      if (result.warning) {
        toastElement?.show({
          severity: "warn",
          summary: "Warning",
          detail: result.warning,
          life: 5000,
        });
      }

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Inpatient ruleset created successfully.",
      });
      navigate("/payer-rules?tab=3");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to create inpatient ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <InpatientRulesetForm
      title="Create Inpatient Ruleset"
      submitLabel="Create Inpatient Ruleset"
      submitIcon="pi pi-check"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
